/**
 * @typedef {Object} CollectionVisit
 * @property {string} url - The collection's storefront path, e.g. `/collections/heart-health`.
 * @property {string} title - The collection's title.
 */

/**
 * Remembers the last collection page (PLP) the shopper visited in this tab,
 * so the PDP breadcrumb can link back to it.
 */
export class LastVisitedCollection {
  /** @static @constant {string} The key used to store the last visited collection in session storage */
  static #STORAGE_KEY = 'lastVisitedCollection';
  /** @static @constant {string} Only collection paths are accepted, which rules out open redirects */
  static #URL_PREFIX = '/collections/';

  /**
   * Stores the collection the shopper is viewing.
   * @param {CollectionVisit} visit - The collection to remember.
   */
  static set({ url, title }) {
    const visit = { url: String(url ?? ''), title: String(title ?? '').trim() };
    if (!this.#isValid(visit)) return;

    try {
      sessionStorage.setItem(this.#STORAGE_KEY, JSON.stringify(visit));
    } catch {
      // Storage can be unavailable (private mode, quota, blocked cookies); the breadcrumb falls back.
    }
  }

  /**
   * Retrieves the last visited collection.
   * @returns {CollectionVisit | null} The stored collection, or null when absent or invalid.
   */
  static get() {
    try {
      const raw = sessionStorage.getItem(this.#STORAGE_KEY);
      if (!raw) return null;

      const visit = JSON.parse(raw);
      return this.#isValid(visit) ? visit : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {any} visit
   * @returns {boolean}
   */
  static #isValid(visit) {
    return (
      Boolean(visit) &&
      typeof visit.url === 'string' &&
      visit.url.startsWith(this.#URL_PREFIX) &&
      typeof visit.title === 'string' &&
      visit.title.trim() !== ''
    );
  }
}

/**
 * Records the collection page it is rendered on as the last visited PLP.
 */
class CollectionVisitRecorder extends HTMLElement {
  connectedCallback() {
    LastVisitedCollection.set({
      url: this.dataset.collectionUrl ?? '',
      title: this.dataset.collectionTitle ?? '',
    });
  }
}

if (!customElements.get('collection-visit-recorder')) {
  customElements.define('collection-visit-recorder', CollectionVisitRecorder);
}
